<script setup lang="ts">
// Panel 1: every site-visit in the current selection, split by what it recorded
// and whether it applied an intervention. One 100% bar states the whole in a
// glance, and the table below it repeats the same five numbers with their
// share, so the bar and the counts cannot drift apart.
import { computed } from "vue";

import type { MapReviewWasteView } from "@/type/api";

import ReportPanel from "../report/ReportPanel.vue";
import {
	formatCount,
	WASTE_OUTCOMES,
	wasteShare,
	wasteTotal,
} from "./wasteTheme";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

const total = computed(() => props.view.totals.visits);

const rows = computed(() =>
	WASTE_OUTCOMES.map((outcome) => {
		const visits = wasteTotal(props.view.totals, outcome.key);
		return {
			...outcome,
			visits,
			share: wasteShare(visits, total.value),
		};
	}),
);
</script>

<template>
	<section class="waste-outcome">
		<ReportPanel title="What a visit produced">
			<p class="panel-help">
				Every site-visit in the current selection, split by what it recorded and
				whether it applied an intervention.
			</p>

			<p v-if="total === 0" class="panel-empty">
				No site-visit in the current selection.
			</p>
			<template v-else>
				<div
					class="split-bar"
					role="img"
					aria-label="Share of site-visits by outcome"
				>
					<div
						v-for="row in rows"
						:key="row.key"
						class="segment"
						:style="{ width: `${row.share}%`, background: row.color }"
						:title="`${row.label}: ${formatCount(row.visits)} visits (${row.share.toFixed(1)}%)`"
					>
						<!-- Only a segment wide enough to hold the figure prints it: a
						     percentage clipped by its own bar is a number the reader
						     cannot read. 12% of the rail is ~44px, which is a
						     five-character label at this size. -->
						<span v-if="row.share >= 12">{{ row.share.toFixed(1) }}%</span>
					</div>
				</div>

				<table class="outcome-table">
					<thead>
						<tr>
							<th>what the visit produced</th>
							<th class="num">visits</th>
							<th class="num">share</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in rows" :key="row.key" :title="row.hint">
							<td>
								<span class="swatch" :style="{ background: row.color }" />
								{{ row.label }}
							</td>
							<td class="num">{{ formatCount(row.visits) }}</td>
							<td class="num">{{ row.share.toFixed(1) }}%</td>
						</tr>
					</tbody>
				</table>
			</template>

			<template #help>
				<p>
					A visit is one Site on one local day. The five rows partition every
					visit: treated after finding production and treated without a finding
					are the visits a kept treatment touched; checked with no production,
					no record at all, and found production but treated nothing are the
					visits no treatment touched.
				</p>
				<p>
					The shares are of the selection's visits, so they move with every
					filter. The vermillion row is the failure case: production was
					recorded and nothing was applied. The grey row is surveillance cost,
					not the same failure: an inspection ran and bought information.
				</p>
			</template>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-outcome {
	grid-column: 1 / -1;
	min-width: 0;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

/* One 100%-stacked bar: the segments are the same five keys as the table
   beneath it, which names each one with its swatch, so the bar needs no legend
   of its own. The white hairline is the boundary between outcomes rather than
   a sixth colour. */
.split-bar {
	display: flex;
	/* One line, always. Rounded percentage widths can sum to a hair over 100%;
	   the segments shrink into the bar rather than wrapping below it, and the
	   bar clips what is left rather than widening the panel. */
	flex-wrap: nowrap;
	align-items: stretch;
	width: 100%;
	min-width: 0;
	height: 44px;
	overflow: hidden;
	border-radius: 3px;
	background: #eef0f2;
}

.segment {
	display: flex;
	align-items: center;
	justify-content: center;
	/* `auto` basis keeps the percentage width; shrink lets the rounding error
	   come out of the segments instead of pushing one off the row. */
	flex: 0 1 auto;
	min-width: 0;
	height: 100%;
	overflow: hidden;
	border-right: 1px solid #fff;
	box-sizing: border-box;
	color: #fff;
	font-size: 0.7rem;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	line-height: 1;
	white-space: nowrap;
}

.segment:last-child {
	border-right: 0;
}

.swatch {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 6px;
	border-radius: 2px;
	vertical-align: -1px;
}

/* The table is the bar's numbers, so it takes the panel's whole width and a
   fixed layout: the three columns divide by rule rather than by the longest
   word a number happens to carry. */
.outcome-table {
	width: 100%;
	table-layout: fixed;
	margin-top: 8px;
	border-collapse: collapse;
	font-size: 0.7rem;
}

.outcome-table th:first-child {
	width: 60%;
}

.outcome-table thead th.num {
	width: 20%;
}

.outcome-table th,
.outcome-table td {
	padding: 4px 6px;
	border-bottom: 1px solid #eef0f2;
	text-align: left;
	vertical-align: middle;
}

.outcome-table th {
	color: #6c757d;
	font-size: 0.64rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.outcome-table tbody tr:last-child td {
	border-bottom: 0;
}

.outcome-table .num {
	text-align: right;
	font-variant-numeric: tabular-nums;
	/* A numeric cell never anchors the table's width: it wraps at the column's
	   edge instead of running the row past the pane. */
	overflow-wrap: anywhere;
}
</style>
